import type { TEventStreamStatus } from '@/store/eventStream'
import type {
    TLiveGameErrorCode,
    TLiveGamePlayer,
    TLiveGameSnapshot,
    TLiveHostMessage,
} from '@/store/liveTrivia/types'

export type TLiveTriviaConnectionStatus = 'idle' | TEventStreamStatus

/**
 * The host video player:
 * - off: the game has no video (or we left it)
 * - connecting: first attempt to reach the video
 * - waitingForHost: the host isn't broadcasting (yet, or any more); retrying
 * - playing: video frames are arriving
 * - ended: the video stopped after the game ended, so we stopped retrying
 */
export type TLiveVideoPlaybackStatus = 'off' | 'connecting' | 'waitingForHost' | 'playing' | 'ended'

type TLiveTriviaState = {
    /** The next not-yet-ended game for the current exam, shown on the Study tab card. */
    upcomingGame: TLiveGameSnapshot | null
    upcomingGameExamGuid: string | null
    /** The game the live view is following, kept current by `/state` and the event stream. */
    game: TLiveGameSnapshot | null
    /** Host messages seen since entering the game, oldest first; the game itself only carries the latest. */
    hostMessages: TLiveHostMessage[]
    player: TLiveGamePlayer | null
    /** The question that knocked the player out, when we saw it happen (unknown if they were out before loading). */
    eliminatedAtQuestionIndex: number | null
    /** serverTime minus local time, so countdowns and timers follow the server's clock. */
    serverClockOffsetMs: number
    connectionStatus: TLiveTriviaConnectionStatus
    isLoadingGame: boolean
    loadGameErrorCode: TLiveGameErrorCode | 'UNKNOWN' | null
    isJoining: boolean
    joinErrorCode: TLiveGameErrorCode | 'UNKNOWN' | null
    /** An answer that's been tapped but not yet accepted, so the UI can lock in immediately. */
    pendingAnswer: { index: number; choiceIndex: number } | null
    answerErrorCode: TLiveGameErrorCode | 'UNKNOWN' | null
    videoPlaybackStatus: TLiveVideoPlaybackStatus
    /** The host's audio and video for the player's <video>; kept raw, since a proxy can't be a srcObject */
    videoStream: MediaStream | null
}

export const getInitialLiveTriviaState = (): TLiveTriviaState => {
    return {
        upcomingGame: null,
        upcomingGameExamGuid: null,
        game: null,
        hostMessages: [],
        player: null,
        eliminatedAtQuestionIndex: null,
        serverClockOffsetMs: 0,
        connectionStatus: 'idle',
        isLoadingGame: false,
        loadGameErrorCode: null,
        isJoining: false,
        joinErrorCode: null,
        pendingAnswer: null,
        answerErrorCode: null,
        videoPlaybackStatus: 'off',
        videoStream: null,
    }
}

const moduleState = getInitialLiveTriviaState()

export default moduleState
